// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import {
  AppIcon,
  DateInput,
  Input,
  Label,
  OptionsSelector,
  Tabs,
} from '@shared/ui-kit';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import type { FC } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import type { DocumentCatalogOption } from '../../hooks/use-document-catalog';
import type { UploadDocumentFormData } from '../../types/upload-document-form';
import { getDocumentCurrencySymbol } from '../../utils/document-currency-symbol';

export interface UploadDocumentFormFieldsProps {
  readonly form: UseFormReturn<UploadDocumentFormData>;
  readonly categorySelectOptions: readonly DocumentCatalogOption[];
  readonly subcategorySelectOptions: readonly DocumentCatalogOption[];
  readonly currencySelectOptions: readonly DocumentCatalogOption[];
  readonly languageSelectOptions: readonly DocumentCatalogOption[];
  readonly isSubmitting: boolean;
  readonly onScopeChange: (scope: DocumentScope) => void;
  readonly onCategoryChange: (category: string) => void;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}

export const UploadDocumentFormFields: FC<UploadDocumentFormFieldsProps> = ({
  form,
  categorySelectOptions,
  subcategorySelectOptions,
  currencySelectOptions,
  languageSelectOptions,
  isSubmitting,
  onScopeChange,
  onCategoryChange,
  t,
}) => {
  const categoryValue = form.watch('category');
  const currencyValue = form.watch('currency');
  const currencyPrefix = getDocumentCurrencySymbol(currencyValue);
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <fieldset
      className={classNames('flex min-w-0 flex-col gap-4 border-0 p-0', {
        'pointer-events-none opacity-60': isSubmitting,
      })}
      disabled={isSubmitting}
    >
      <fieldset className="flex flex-col gap-2">
        <Label required>
          <Translation
            i18nKey="documents.upload-drawer.fields.scope"
            ns="components"
          />
        </Label>
        <Controller
          control={control}
          name="scope"
          render={({ field }) => (
            <Tabs.Root
              baseId="upload-document-scope"
              defaultValue={field.value}
              controlled
              onTabChange={value => {
                const scope = value as DocumentScope;
                field.onChange(scope);
                onScopeChange(scope);
              }}
              variant="segmented"
              size="sm"
            >
              <Tabs.List>
                <Tabs.Trigger value={DocumentScope.Personal}>
                  <Translation i18nKey="documents.scope.personal" ns="components" />
                </Tabs.Trigger>
                <Tabs.Trigger value={DocumentScope.Company}>
                  <Translation i18nKey="documents.scope.company" ns="components" />
                </Tabs.Trigger>
              </Tabs.List>
            </Tabs.Root>
          )}
        />
      </fieldset>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <fieldset className="flex flex-col gap-2">
          <Label required>
            <Translation
              i18nKey="documents.upload-drawer.fields.category"
              ns="components"
            />
          </Label>
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <OptionsSelector
                optionsAvailable={[...categorySelectOptions]}
                optionSelected={field.value}
                onChangeValueSelected={value => {
                  field.onChange(value);
                  onCategoryChange(value);
                }}
                defaultText={t('documents.upload-drawer.placeholders.category', {
                  ns: 'components',
                })}
                selectType="white"
                data-testid="upload-document-category"
              />
            )}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <Label required>
            <Translation
              i18nKey="documents.upload-drawer.fields.subcategory"
              ns="components"
            />
          </Label>
          <Controller
            control={control}
            name="subcategory"
            render={({ field }) => (
              <OptionsSelector
                optionsAvailable={[...subcategorySelectOptions]}
                optionSelected={field.value}
                onChangeValueSelected={field.onChange}
                defaultText={t('documents.upload-drawer.placeholders.subcategory', {
                  ns: 'components',
                })}
                selectType="white"
                disabled={!categoryValue}
                data-testid="upload-document-subcategory"
              />
            )}
          />
        </fieldset>
      </div>

      <fieldset className="flex flex-col gap-2">
        <Label required>
          <Translation
            i18nKey="documents.upload-drawer.fields.title"
            ns="components"
          />
        </Label>
        <Input
          {...register('title')}
          error={Boolean(errors.title)}
          data-testid="upload-document-title"
        />
      </fieldset>

      <Accordion
        disableGutters
        elevation={0}
        className="!rounded-lg !bg-section !shadow-none before:!hidden"
        data-testid="upload-document-additional-details"
      >
        <AccordionSummary
          expandIcon={<AppIcon icon="ChevronDown" size="base" color="secondary" />}
          className="!min-h-0 !px-4 !py-3"
        >
          <Typography
            variant="body2"
            className="!font-medium uppercase !text-primary-medium"
          >
            <Translation
              i18nKey="documents.upload-drawer.additional-details"
              ns="components"
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="flex flex-col gap-4 !px-4 !pb-4 !pt-0">
          <fieldset className="flex flex-col gap-2">
            <Label>
              <Translation
                i18nKey="documents.upload-drawer.fields.issuer"
                ns="components"
              />
            </Label>
            <Input {...register('issuer')} data-testid="upload-document-issuer" />
          </fieldset>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <fieldset className="flex flex-col gap-2">
              <Label>
                <Translation
                  i18nKey="documents.upload-drawer.fields.amount"
                  ns="components"
                />
              </Label>
              <Controller
                control={control}
                name="amount"
                render={({ field }) => (
                  <NumericFormat
                    customInput={Input}
                    value={field.value}
                    onValueChange={({ value }) => field.onChange(value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                    data-testid="upload-document-amount"
                  />
                )}
              />
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <Label>
                <Translation
                  i18nKey="documents.upload-drawer.fields.currency"
                  ns="components"
                />
              </Label>
              <Controller
                control={control}
                name="currency"
                render={({ field }) => (
                  <OptionsSelector
                    optionsAvailable={[...currencySelectOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t(
                      'documents.upload-drawer.placeholders.currency',
                      {
                        ns: 'components',
                      },
                    )}
                    selectType="white"
                    data-testid="upload-document-currency"
                  />
                )}
              />
            </fieldset>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <fieldset className="flex flex-col gap-2">
              <Label>
                <Translation
                  i18nKey="documents.upload-drawer.fields.document-date"
                  ns="components"
                />
              </Label>
              <Controller
                control={control}
                name="document_date"
                render={({ field }) => (
                  <DateInput
                    value={field.value}
                    onChange={field.onChange}
                    data-testid="upload-document-date"
                  />
                )}
              />
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <Label>
                <Translation
                  i18nKey="documents.upload-drawer.fields.expiry-date"
                  ns="components"
                />
              </Label>
              <Controller
                control={control}
                name="expiry_date"
                render={({ field }) => (
                  <DateInput
                    value={field.value}
                    onChange={field.onChange}
                    data-testid="upload-document-expiry"
                  />
                )}
              />
            </fieldset>
          </div>

          <fieldset className="flex flex-col gap-2">
            <Label>
              <Translation
                i18nKey="documents.upload-drawer.fields.language"
                ns="components"
              />
            </Label>
            <Controller
              control={control}
              name="language"
              render={({ field }) => (
                <OptionsSelector
                  optionsAvailable={[...languageSelectOptions]}
                  optionSelected={field.value ?? ''}
                  onChangeValueSelected={field.onChange}
                  defaultText={t('documents.upload-drawer.placeholders.language', {
                    ns: 'components',
                  })}
                  selectType="white"
                  data-testid="upload-document-language"
                />
              )}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-2">
            <Label>
              <Translation
                i18nKey="documents.upload-drawer.fields.description"
                ns="components"
              />
            </Label>
            <textarea
              {...register('description')}
              rows={4}
              className="input-common min-h-24 resize-y"
              data-testid="upload-document-description"
            />
          </fieldset>
        </AccordionDetails>
      </Accordion>
    </fieldset>
  );
};
